import { useState } from 'react';
import { useTranslation } from 'react-i18next';

export default function ContactForm() {
  const { t } = useTranslation();
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const message = (form.elements.namedItem('message') as HTMLTextAreaElement).value;
    if (message.length > 500) return;

    setFormStatus('submitting');

    const data = new URLSearchParams();
    const formData = new FormData(form);
    formData.forEach((value, key) => {
      data.append(key, value as string);
    });

    try {
      const response = await fetch('https://readdy.ai/api/form/d6kg15c6fojmejgaijr0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });

      if (response.ok) {
        setFormStatus('success');
        form.reset();
        setCharCount(0);
      } else {
        setFormStatus('error');
      }
    } catch {
      setFormStatus('error');
    }
  };

  return (
    <form
      id="contact-form-main"
      data-readdy-form
      onSubmit={handleSubmit}
      className="space-y-6"
    >
      <div className="grid md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('form_name')} <span className="text-amber-700">{t('form_required')}</span>
          </label>
          <input
            type="text"
            name="name"
            required
            placeholder={t('form_name_placeholder')}
            className="w-full border border-gray-200 rounded-md px-4 py-3 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-amber-600 focus:border-transparent transition"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('form_email')} <span className="text-amber-700">{t('form_required')}</span>
          </label>
          <input
            type="email"
            name="email"
            required
            placeholder={t('form_email_placeholder')}
            className="w-full border border-gray-200 rounded-md px-4 py-3 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-amber-600 focus:border-transparent transition"
          />
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('form_phone')}
          </label>
          <input
            type="tel"
            name="phone"
            placeholder={t('form_phone_placeholder')}
            className="w-full border border-gray-200 rounded-md px-4 py-3 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-amber-600 focus:border-transparent transition"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('form_subject')} <span className="text-amber-700">{t('form_required')}</span>
          </label>
          <select
            name="subject"
            required
            className="w-full border border-gray-200 rounded-md px-4 py-3 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-amber-600 focus:border-transparent transition bg-white cursor-pointer"
          >
            <option value="">{t('form_subject_placeholder')}</option>
            <option value="General Inquiry">{t('form_subject_general')}</option>
            <option value="Sell My Items">{t('form_subject_sell')}</option>
            <option value="Appraisal Request">{t('form_subject_appraisal')}</option>
            <option value="Restoration Services">{t('form_subject_restoration')}</option>
            <option value="Sourcing Request">{t('form_subject_sourcing')}</option>
            <option value="Prop & Event Rental">{t('form_subject_prop')}</option>
            <option value="Other">{t('form_subject_other')}</option>
          </select>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {t('form_message')} <span className="text-amber-700">{t('form_required')}</span>
        </label>
        <textarea
          name="message"
          required
          rows={6}
          maxLength={500}
          placeholder={t('form_message_placeholder')}
          onChange={(e) => setCharCount(e.target.value.length)}
          className="w-full border border-gray-200 rounded-md px-4 py-3 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-amber-600 focus:border-transparent transition resize-none"
        />
        <p className={`text-xs mt-1 text-right ${charCount > 480 ? 'text-red-500' : 'text-gray-400'}`}>
          {charCount}/500
        </p>
      </div>

      {/* Photo email note */}
      <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-md px-5 py-4">
        <div className="w-5 h-5 flex items-center justify-center mt-0.5 shrink-0">
          <i className="ri-camera-line text-amber-700 text-lg"></i>
        </div>
        <p className="text-sm text-amber-800">
          {t('form_photo_note')}{' '}
          <a
            href="mailto:dawoodhas334@gmail.com"
            className="font-semibold underline hover:text-amber-900 transition cursor-pointer"
          >
            dawoodhas334@gmail.com
          </a>
        </p>
      </div>

      {formStatus === 'success' && (
        <div className="flex items-center gap-3 bg-green-50 border border-green-200 text-green-700 rounded-md px-5 py-4">
          <div className="w-5 h-5 flex items-center justify-center">
            <i className="ri-checkbox-circle-line text-xl"></i>
          </div>
          <p className="text-sm font-medium">{t('form_success')}</p>
        </div>
      )}

      {formStatus === 'error' && (
        <div className="flex items-center gap-3 bg-red-50 border border-red-200 text-red-700 rounded-md px-5 py-4">
          <div className="w-5 h-5 flex items-center justify-center">
            <i className="ri-error-warning-line text-xl"></i>
          </div>
          <p className="text-sm font-medium">{t('form_error')}</p>
        </div>
      )}

      <div className="text-center">
        <button
          type="submit"
          disabled={formStatus === 'submitting' || charCount > 500}
          className="bg-amber-700 hover:bg-amber-800 disabled:bg-amber-400 text-white px-12 py-3 rounded-md text-sm font-semibold transition-all whitespace-nowrap cursor-pointer inline-flex items-center gap-2"
        >
          {formStatus === 'submitting' ? (
            <>
              <i className="ri-loader-4-line animate-spin"></i>
              {t('form_sending')}
            </>
          ) : (
            <>
              <i className="ri-send-plane-line"></i>
              {t('form_send')}
            </>
          )}
        </button>
      </div>
    </form>
  );
}
